import { filter } from 'lodash';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, tap } from 'rxjs';

import {
  EventTypeEnum,
  CorporateNotificationType,
  CorporateNotificationFlagType,
  CorporateNotificationUserFlagType,
  GetCorporateNotificationFlagsByTargetGQL,
  CountUnseenCorporateNotificationsForUserGQL,
  MarkAllCorporateNotificationsAsSeenForUserGQL,
} from '@sifca-monorepo/widget-generator';
import { GetCorporateNotificationsByUserGQL } from '@sifca-monorepo/widget-generator';
import {
  CreateCorporateNotificationUserFlagGQL,
  DeleteCorporateNotificationUserFlagGQL,
  GetUserCorporateNotificationFlagsGQL,
} from '@sifca-monorepo/terminal-generator';

interface CorporateNotificationFlagTypeWithEnable extends CorporateNotificationFlagType {
  enable: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class MobileNotificationsService {
  private isLastPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private unreadNotif: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private loadingFlags: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private loadingNotifications: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private notifications: BehaviorSubject<CorporateNotificationType[]> = new BehaviorSubject<CorporateNotificationType[]>([]);
  private notifFlags: BehaviorSubject<CorporateNotificationFlagTypeWithEnable[]> = new BehaviorSubject<CorporateNotificationFlagTypeWithEnable[]>([]);
  private userFlags: BehaviorSubject<CorporateNotificationUserFlagType[]> = new BehaviorSubject<CorporateNotificationUserFlagType[]>([]);

  pageIndex = 0;
  pageLimit = 10;

  get userFlags$(): Observable<CorporateNotificationUserFlagType[]> {
    return this.userFlags.asObservable();
  }

  get unreadNotif$(): Observable<boolean> {
    return this.unreadNotif.asObservable();
  }
  set unreadNotif$(value: any) {
    this.unreadNotif.next(value);
  }

  get notifications$(): Observable<CorporateNotificationType[]> {
    return this.notifications.asObservable();
  }
  set notifications$(value: any) {
    this.notifications.next(value);
  }

  get notifFlags$(): Observable<CorporateNotificationFlagTypeWithEnable[]> {
    return this.notifFlags.asObservable();
  }

  get loadingFlags$(): Observable<boolean> {
    return this.loadingFlags.asObservable();
  }

  get isLastPage$(): Observable<boolean> {
    return this.isLastPage.asObservable();
  }

  get loadingNotifications$(): Observable<boolean> {
    return this.loadingNotifications.asObservable();
  }

  constructor(
    private getCorporateNotificationsByUserGQL: GetCorporateNotificationsByUserGQL,
    private getUserCorporateNotificationFlagsGQL: GetUserCorporateNotificationFlagsGQL,
    private createCorporateNotificationUserFlagGQL: CreateCorporateNotificationUserFlagGQL,
    private deleteCorporateNotificationUserFlagGQL: DeleteCorporateNotificationUserFlagGQL,
    private getCorporateNotificationFlagsByTargetGQL: GetCorporateNotificationFlagsByTargetGQL,
    private countUnseenCorporateNotificationsForUserGQL: CountUnseenCorporateNotificationsForUserGQL,
    private markAllCorporateNotificationsAsSeenForUserGQL: MarkAllCorporateNotificationsAsSeenForUserGQL,
  ) {}

  getCorporateNotificationFlagsWithStatus(): Observable<CorporateNotificationFlagTypeWithEnable[]> {
    this.loadingFlags.next(true);
  
    const userFlags$ = this.getUserCorporateNotificationFlagsGQL
      .fetch({ user: localStorage.getItem('elevkusr') })
      .pipe(map(({ data }: any) => data.getUserCorporateNotificationFlags as CorporateNotificationUserFlagType[]));
  
    const notifFlags$ = this.getCorporateNotificationFlagsByTargetGQL
      .fetch({ target: { pos: localStorage.getItem('posId') } })
      .pipe(map(({ data }: any) => data.getCorporateNotificationFlagsByTarget as CorporateNotificationFlagType[]));
  
    return forkJoin([userFlags$, notifFlags$]).pipe(
      map(([userFlags, notifFlags]) => {
        this.userFlags.next(userFlags);
  
        const userFlagIds = new Set(userFlags.map((uf) => uf.flag?.id ?? uf.id));
  
        const enrichedFlags = notifFlags.map((flag) => ({
          ...flag,
          enable: !userFlagIds.has(flag.id),
        }));
  
        this.notifFlags.next(enrichedFlags);
        this.loadingFlags.next(false);
  
        console.log('🚀 enrichedFlags:', enrichedFlags);
        return enrichedFlags;
      }),
    );
  }

  createCorporateNotificationUserFlag(flag: string): Observable<CorporateNotificationUserFlagType> {
    return this.createCorporateNotificationUserFlagGQL.mutate({ input: { flag, user: localStorage.getItem('elevkusr') } }).pipe(
      map(({ data }: any) => {
        const index = this.notifFlags.value.findIndex((item) => item.id === flag);
        let flags = this.notifFlags.value ;
        flags[index].enable = false;
        this.notifFlags.next(flags);
        return data.deleteCorporateNotificationUserFlag;
      }),
    );
  }

  deleteCorporateNotificationUserFlag(id: string): Observable<any> {
    return this.deleteCorporateNotificationUserFlagGQL.mutate({ id }).pipe(
      map(({ data }: any) => {
        const index = this.notifFlags.value.findIndex((flag) => flag.id === id);
        let flags = this.notifFlags.value ;
        flags[index].enable = true;
        this.notifFlags.next(flags);
        return data.deleteCorporateNotificationUserFlag;
      }),
    );
  }

  getCorporateNotificationsByUser(reset = true): Observable<CorporateNotificationType[]> {
    if (reset) {
      this.loadingNotifications.next(true);
    }
    return this.getCorporateNotificationsByUserGQL
      .fetch({ target: { pos: localStorage.getItem('posId') }, pagination: { page: this.pageIndex, limit: this.pageLimit } })
      .pipe(
        map(({ data }: any) => {
          this.notifications.next(filter(data.getCorporateNotificationsByUser.objects, (notif) => notif.event !== EventTypeEnum.OUTBOUND_WIDGET));
          this.loadingNotifications.next(false);
          this.isLastPage.next(data.getCorporateNotificationsByUser.isLast);
          return data.getCorporateNotificationsByUser.objects;
        }),
      );
  }

  markAllCorporateNotificationsAsSeenForUser(): Observable<any> {
    return this.markAllCorporateNotificationsAsSeenForUserGQL.mutate({ target: { pos: localStorage.getItem('posId') } }).pipe(
      tap(() => {
        this.unreadNotif.next(false);
      }),
      catchError((err) => {
        this.unreadNotif.next(false);
        return of(null);
      }),
    );
  }

  countUnseenCorporateNotificationsForUser(): Observable<number> {
    return this.countUnseenCorporateNotificationsForUserGQL.fetch({ target: { pos: localStorage.getItem('posId') } }).pipe(
      map(({ data }) => {
        this.unreadNotif.next(data.countUnseenCorporateNotificationsForUser.count > 0);
        return data.countUnseenCorporateNotificationsForUser.count;
      }),
    );
  }
}
